import { skipToken } from "@reduxjs/toolkit/query";
import { getNotarizationInfoMessage } from "../pages/notarization/utils";
import {
  useGetDocumentQuery,
  useGetDocumentsByUserIdQuery,
  useGetNotarizationsByDocumentIdQuery,
} from "../api/documents";
import { is7DayRuleEnabled } from "../pages/notarization/utils";
import { useGetCurrentUserQuery } from "../api/users";
import { formatUserFirstName } from "../utils/format";

export enum RepresentationType {
  NO_REPRESENTATION = "NO_REPRESENTATION",
  PARTIAL_REPRESENTATION = "PARTIAL_REPRESENTATION",
  FULL_REPRESENTATION = "FULL_REPRESENTATION",
}

export const useNotarizationState = (documentId?: string) => {
  const {
    data: user,
    isLoading: isUserLoading,
    error: userError,
  } = useGetCurrentUserQuery();

  const {
    data: userDocuments,
    isLoading: isUserDocumentsLoading,
    error: userDocumentsError,
  } = useGetDocumentsByUserIdQuery(
    user?.id && !documentId ? user.id : skipToken,
  );

  const {
    data: specificDocument,
    isLoading: isSpecificDocumentLoading,
    error: specificDocumentError,
  } = useGetDocumentQuery(documentId ?? skipToken);

  const isDocumentLoading = documentId
    ? isSpecificDocumentLoading
    : isUserDocumentsLoading;
  const documentError = documentId ? specificDocumentError : userDocumentsError;

  const document = documentId
    ? specificDocument
    : userDocuments
      ? userDocuments[0]
      : null;

  const {
    data: notarizations,
    isLoading: isNotarizationsLoading,
    error: notarizationsError,
  } = useGetNotarizationsByDocumentIdQuery(document?.id ?? skipToken);

  const isDocumentGenerated = !!document?.lastGeneratedAt;

  const isAttorneyAPurchased =
    !!document?.purchaseItems &&
    !!document.purchaseItems.find(
      (item) =>
        item.product.productType === "REPRESENTATION" &&
        item.userId === document.userAId &&
        item.paymentStatus === "SUCCESSFUL" &&
        item.deletedAt === null,
    );
  const isAttorneyBPurchased =
    !!document?.purchaseItems &&
    !!document.purchaseItems.find(
      (item) =>
        item.product.productType === "REPRESENTATION" &&
        item.userId === document.userBId &&
        item.paymentStatus === "SUCCESSFUL" &&
        item.deletedAt === null,
    );
  const isAttorneyRequiredUserA =
    !!document?.userAWillHireAttorney || isAttorneyAPurchased;
  const isAttorneyRequiredUserB =
    !!document?.userBWillHireAttorney || isAttorneyBPurchased;

  const isAttorneyABooked = !!document?.attorneyAId;
  const isAttorneyBBooked = !!document?.attorneyBId;

  const isAttorneyApproved = document?.status === "FULLY_APPROVED";

  const isNotarizationPurchased =
    !!document?.purchaseItems &&
    !!document.purchaseItems.find(
      (item) =>
        item.product.productType === "NOTARIZATION" &&
        item.paymentStatus === "SUCCESSFUL" &&
        item.deletedAt === null,
    );

  const isDocumentReviewed = !!document?.reviewedAt;
  const isDocumentFinalized = !!document?.finalizedAt;
  const isWitnessRequired =
    !!document?.template &&
    !!document.template.documentTemplateStates.find(
      (state) => state.stateId === document.stateId,
    )?.witnessRequired;
  const isOnlineNotaryEnabled =
    !!document?.state && !!document.state.allowsOnlineNotary;

  let representationType = RepresentationType.NO_REPRESENTATION;
  if (isAttorneyRequiredUserA && isAttorneyRequiredUserB) {
    representationType = RepresentationType.FULL_REPRESENTATION;
  } else if (isAttorneyRequiredUserA || isAttorneyRequiredUserB) {
    representationType = RepresentationType.PARTIAL_REPRESENTATION;
  }

  const isNotarizationComplete =
    !!notarizations && !!notarizations[0]?.notarizationCompletedAt;
  const isNotarizationFileUploaded =
    !!notarizations && !!notarizations[0]?.notarizedFileId;

  const isNotarizationInProgress =
    !!notarizations?.length && !isNotarizationComplete;

  const is7DayRuleWaiting = is7DayRuleEnabled(
    document,
    isAttorneyRequiredUserA || isAttorneyRequiredUserB,
  );

  const notarizationInfoMessage = getNotarizationInfoMessage({
    isAttorneyRequiredUserA,
    isAttorneyRequiredUserB,
    isAttorneyABooked,
    isAttorneyBBooked,
    isAttorneyApproved,
    isDocumentGenerated,
    isDocumentReviewed,
    isDocumentFinalized,
    error: userError ?? documentError ?? notarizationsError,
    isUserA: user?.id === document?.userAId,
    partnerName: formatUserFirstName(
      user?.id === document?.userAId ? document?.userB : document?.userA,
    ),
  });

  return {
    isLoading: isUserLoading || isDocumentLoading || isNotarizationsLoading,
    error: userError ?? documentError ?? notarizationsError,
    notarizationInfoMessage,
    isDocumentGenerated,
    isAttorneyRequiredUserA,
    isAttorneyRequiredUserB,
    isAttorneyABooked,
    isAttorneyBBooked,
    isAttorneyApproved,
    isWitnessRequired,
    isOnlineNotaryEnabled,
    userAHasAttorney: !!document?.attorneyAId,
    userBHasAttorney: !!document?.attorneyBId,
    representationType,
    isNotarizationPurchased,
    isNotarizationComplete,
    isNotarizationFileUploaded,
    is7DayRuleWaiting,
    isDocumentReviewed,
    isDocumentFinalized,
    document,
    isNotarizationInProgress,
    user,
    isAttorneyAPurchased,
    isAttorneyBPurchased,
  };
};
